/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getLocalizedTime from "@tIntermatic/Utilities/getLocalizedTime";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    openTime = "openTime",
    closeTime = "closeTime",
    title = "title",
    phoneNumber = "phoneNumber",
    showOpenCloseBadge = "showOpenCloseBadge",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.openTime]: Date;
        [fields.closeTime]: Date;
        [fields.title]: string;
        [fields.phoneNumber]: string;
        [fields.showOpenCloseBadge]: boolean;
    };
}

const checkIsOpen = (openTimeDate: Date, closeTimeDate: Date) => {
    let isOpen = false;
    const currentDate = new Date();
    const openDate = new Date(currentDate.getTime());
    const closeDate = new Date(currentDate.getTime());
    const currentWeekDay = currentDate.getDay();
    const openWeekDay = openTimeDate.getDay();
    const closeWeekDay = closeTimeDate.getDay() === 0 ? 7 : closeTimeDate.getDay();
    openDate.setHours(openTimeDate.getHours());
    openDate.setMinutes(openTimeDate.getMinutes());
    closeDate.setHours(closeTimeDate.getHours());
    closeDate.setMinutes(closeTimeDate.getMinutes());
    isOpen =
        openWeekDay <= currentWeekDay &&
        closeWeekDay >= currentWeekDay &&
        openDate <= currentDate &&
        closeDate >= currentDate;
    return isOpen;
};

const getTimeZoneAbbreviation = (timeZoneDate: string) => {
    const matches = timeZoneDate?.match(/\b(\w)/g);
    const acronym = matches?.join("");
    return acronym;
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const currentDate = new Date();
    const timeZoneOptions: Intl.DateTimeFormatOptions = {
        timeZoneName: "long",
    };
    const timeZoneDate = getLocalizedDateTime({
        dateTime: currentDate,
        language: state.context.session.language,
        options: timeZoneOptions,
    });
    const openTimeOptions: Intl.DateTimeFormatOptions = {
        hour12: true,
        hour: "numeric",
        minute: "numeric",
    };
    const closeTimeOptions: Intl.DateTimeFormatOptions = {
        hour12: true,
        hour: "numeric",
        minute: "numeric",
    };
    const openDayOptions: Intl.DateTimeFormatOptions = {
        weekday: "long",
    };
    const closeDayOptions: Intl.DateTimeFormatOptions = {
        weekday: "long",
    };

    const openTimeDate = new Date(ownProps.fields.openTime);
    const closeTimeDate = new Date(ownProps.fields.closeTime);
    const openTime = getLocalizedTime({
        dateTime: openTimeDate,
        language: state.context.session.language,
        options: openTimeOptions,
    });
    const closeTime = getLocalizedTime({
        dateTime: closeTimeDate,
        language: state.context.session.language,
        options: closeTimeOptions,
    });
    const openDay = getLocalizedDateTime({
        dateTime: openTimeDate,
        language: state.context.session.language,
        options: openDayOptions,
    });
    const closeDay = getLocalizedDateTime({
        dateTime: closeTimeDate,
        language: state.context.session.language,
        options: closeDayOptions,
    });

    return {
        openDay,
        closeDay,
        openTime,
        closeTime,
        timeZone: getTimeZoneAbbreviation(timeZoneDate.split(",")[1]),
        isOpen: checkIsOpen(openTimeDate, closeTimeDate),
        language: state.context.session.language,
    };
};
export const contactTimingStyles = {
    textStyle: {
        css: css`
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 24px;
            color: #33333a;
            display: block;
        `,
    },

    openCloseWrapper: {
        css: css`
            font-size: 12px;
            letter-spacing: 0;
            line-height: 14px;
            color: #ffffff;
            border-radius: 100px;
            background-color: #006bc0;
            padding: 8px 10px;
            text-transform: uppercase;
            width: fit-content;
            margin-top: 11px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: 600;
                    `,
                    css`
                        font-weight: 600;
                    `,
                    css`
                        font-weight: bold;
                    `,
                    css`
                        font-weight: bold;
                    `,
                    css`
                        font-weight: bold;
                    `,
                ])}
        `,
    },

    titleCss: {
        css: css`
            display: block;
            font-size: 21px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 25px;
            color: #33333a;
            padding-bottom: 10px;
        `,
    },

    phoneNumber: {
        css: css`
            display: block;
            font-size: 16px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 24px;
            color: #006bc0;
        `,
    },
};

const styles = contactTimingStyles;
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const INTContactTiming: React.FC<Props> = ({
    openTime,
    closeTime,
    openDay,
    closeDay,
    isOpen,
    timeZone,
    fields,
}: Props) => {
    if (!openTime || !closeTime) {
        return null;
    }
    return (
        <>
            <StyledWrapper>
                {fields?.title && <Typography {...styles.titleCss}>{fields?.title}</Typography>}
                {fields?.phoneNumber && (
                    <Link href={`tel:${fields?.phoneNumber}`} {...styles.phoneNumber} target="_blank">
                        {fields?.phoneNumber}
                    </Link>
                )}
                <Typography {...styles.textStyle}>
                    {openDay} - {closeDay}
                </Typography>
                <Typography {...styles.textStyle}>
                    {openTime} - {closeTime} {timeZone}
                </Typography>
                {fields?.showOpenCloseBadge && (
                    <StyledWrapper {...styles.openCloseWrapper}>
                        {translate("Currently")}{" "}
                        {isOpen ? (
                            <Typography>{translate("Open")}</Typography>
                        ) : (
                            <Typography>{translate("Closed")}</Typography>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(INTContactTiming),
    definition: {
        group: "Basic",
        icon: "PageTitle",
        displayName: "ContactTiming",
        fieldDefinitions: [
            {
                name: fields.openTime,
                defaultValue: new Date(),
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
                validate: (value, item) => {
                    const closeDate = new Date(item.fields.closeTime);
                    return value && value < closeDate ? null : "Please enter a valid open time.";
                },
            },

            {
                name: fields.closeTime,
                defaultValue: new Date(),
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 2,
                validate: (value, item) => {
                    const openDate = new Date(item.fields.openTime);
                    return value && value > openDate ? null : "Please enter a valid close time.";
                },
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 3,
                fieldType: "Translatable",
            },
            {
                name: fields.phoneNumber,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 4,
                fieldType: "Translatable",
            },
            {
                name: fields.showOpenCloseBadge,
                displayName: "Show Open Close Badge",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                sortOrder: 5,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
